import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {DropdownOption} from 'src/app/models/dropdown-option.model';
import {
  AddCategoriesComponent
} from '../../components/add-categories/add-categories.component';
import {debounceTime, Subject, switchMap, takeUntil} from 'rxjs';
import {
  addResources
} from 'src/app/modules/shared/constants/validation.constants';
import {CommonService} from 'src/app/modules/shared/services/common.service';
import {
  CategoriesResponse,
  ResourceDetails,
  Section
} from '../../model/resource-model';
import {Store} from '@ngrx/store';
import {
  getAddResourcesDetails,
  getCategoriesList,
  getGroupsList,
  getOrganisationsList,
  getRolesList,
  getSessionList
} from '../../core/resources-hub.selectors';
import {
  ClearSelectedResource,
  DeleteResourceRequest,
  FetchCategoriesListRequest,
  FetchRelatedResourcesRequest,
  FetchResourceDetailsForEditRequest,
  FetchGroupsListRequest,
  FetchOrganisationsListRequest,
  FetchRolesListRequest,
  FetchSessionListRequest,
  FetchSubCategoriesListRequest,
  SaveOrPublishResourceRequest,
  UpdateHeroSectionDetailsAddResources,
  UpdateSectionDetailsAddResources
} from '../../core/resources-hub.actions';
import {v4 as uuidv4} from 'uuid';
import {
  RelatedResourcesComponent
} from '../../components/related-resources/related-resources.component';
import {
  CreateResourceStatus
} from '../../constants/create-resource-status.enum';
import {ResourcesHubService} from "../../core/resources-hub.service";
import {ActivatedRoute, Router} from "@angular/router";
import * as Validation from "../../../../shared/constants/validation.constants";
import {SharedService} from "../../../../shared/core/shared.service";

@Component({
  selector: 'app-add-resource-screen',
  templateUrl: './add-resource-screen.component.html',
  styleUrls: ['./add-resource-screen.component.css']
})
export class AddResourceScreenComponent implements OnInit, OnDestroy {
  resourceDetails: ResourceDetails = {}
  validation = Validation;
  addResourceForm: FormGroup = new FormGroup({
    title: new FormControl('', [
      Validators.required,
      Validators.minLength(addResources.title.length.min),
      Validators.maxLength(addResources.title.length.max)
    ]),
    description: new FormControl('', [Validators.maxLength(addResources.description.length.max)]),
    sessions: new FormControl([]),
    categories: new FormControl([]),
    roles: new FormControl([], Validators.required,),
    organisations: new FormControl([]),
    groups: new FormControl([]),
    relatedResources: new FormControl([]),
    imageUrl: new FormControl('', [Validators.required]),
  });

  errorMessages: any = {};
  sessionLoading: boolean = false;
  rolesLoading: boolean = false;
  organisationsLoading: boolean = false;
  groupsLoading: boolean = false;
  organisationOptions: DropdownOption[] = [];
  groupOptions: DropdownOption[] = [];
  allOrganisations: boolean = true;
  allGroups: boolean = true;

  errorMessageMap = {
    title: {
      required: 'This field is required',
      minlength: `Minimum ${addResources.title.length.min} characters required`,
      maxlength: `Maximum ${addResources.title.length.max} characters accepted`
    },
    description: {
      maxlength: `Maximum ${addResources.description.length.max} characters accepted`
    },
    sessions: {},
    categories: {},
    roles: {
      required: 'This field is required',
    },
    imageUrl: {
      required: 'This field is required',
    },
  }

  relatedResourceDisplayColumn: any[] = [
    'title',
    'category',
    'subCategories',
  ]

  sessionDropDownList: DropdownOption[] = []

  roleOptions: DropdownOption[] = [];
  allowExtension = [
    'jpeg', 'jpg', 'image/jpg', 'image/png', 'image/jpeg', 'png', 'image/webp',
    'webp'
  ];
  categoriesData: CategoriesResponse[] = [];
  selectedCategoriesDataMap: any[] = [];
  isSavedAsDraft: boolean = false;
  mode: any = 'add';
  isUserNavigatedFirstTime: boolean = true;
  imageLoading: boolean = false
  protected readonly Validation = Validation;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(private matDialog: MatDialog, private commonService: CommonService, private store: Store, private resourceHubService: ResourcesHubService, private route: ActivatedRoute, private router: Router, private sharedService: SharedService) {
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.onDestroy))
      .subscribe((params: any) => {
        if (params.params.resourceId && this.router.url.includes('edit-resource')) {
          this.mode = 'edit'
          this.store.dispatch(FetchResourceDetailsForEditRequest({payload: {resourceId: params.params.resourceId, slug: params.params.slug}}))
        } else {
          this.store.dispatch(ClearSelectedResource());
        }
      })
    this.store.dispatch(FetchSessionListRequest());
    this.store.dispatch(FetchRolesListRequest());
    this.store.dispatch(FetchOrganisationsListRequest());
    this.store.dispatch(FetchGroupsListRequest({}));
    this.store.dispatch(FetchCategoriesListRequest());
    this.store.dispatch(FetchRelatedResourcesRequest({payload: {pageNumber: 1, filters: {searchText: ''}}}))
    this.store.select(getSessionList).pipe(takeUntil(this.onDestroy))
      .subscribe(sessions => {
        this.sessionLoading = sessions.loading
        const sessionList: any[] = []
        sessions.list.map(session => {
          sessionList.push({
            key: session.id,
            value: "Session " + session.id
          })
        })
        this.sessionDropDownList = sessionList;
      })

    this.store.select(getRolesList).subscribe(roles => {
      this.rolesLoading = roles.loading
      this.roleOptions = roles.list;
    })

    this.store.select(getOrganisationsList).pipe(takeUntil(this.onDestroy))
      .subscribe(orgs => {
        this.organisationsLoading = orgs.loading;
        this.organisationOptions = orgs.list;
      })

    this.store.select(getGroupsList).pipe(takeUntil(this.onDestroy))
      .subscribe(groups => {
        this.groupsLoading = groups.loading;
        this.groupOptions = groups.list;
      })

    // Re-fetch groups when organisations change
    this.addResourceForm.get('organisations').valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(selectedOrgs => {
        if (selectedOrgs?.length) {
          this.store.dispatch(FetchGroupsListRequest({payload: {clientId: selectedOrgs}}));
        } else {
          this.store.dispatch(FetchGroupsListRequest({}));
        }
      })

    this.store.select(getAddResourcesDetails).pipe(takeUntil(this.onDestroy))
      .subscribe((details: any) => {
        this.resourceDetails = details;
        setTimeout(()=>{
          if(this.resourceHubService.lastUpdatedId.value){
            document.getElementById(this.resourceHubService.lastUpdatedId.value)?.scrollIntoView({ behavior: "smooth" });
          }
        },0)
        if (this.mode === 'edit' && this.isUserNavigatedFirstTime && Object.keys(details).length > 0) {
          this.fetchSubCategoriesData();
          this.isUserNavigatedFirstTime = false;

          // Set org/group checkbox state based on saved data
          if (details.organisations?.length) {
            this.allOrganisations = false;
          }
          if (details.groups?.length) {
            this.allGroups = false;
          }
        }
        if (this.resourceHubService.isSavedAsDraft) {
          this.resourceHubService.setSaveAsDraft(false);
        }

        this.addResourceForm.patchValue({
          ...details
        }, {emitEvent: false})

      });

    this.addResourceForm.valueChanges.pipe(debounceTime(1000), takeUntil(this.onDestroy))
      .subscribe(heroSectionForm => {
        this.resourceHubService.setLastUpdatedId(null)
        this.store.dispatch(UpdateHeroSectionDetailsAddResources({data: heroSectionForm}))
      })

    this.store.select(getCategoriesList).pipe(takeUntil(this.onDestroy))
      .subscribe(categories => {
        this.categoriesData = categories;
      });

    this.resourceHubService.isSavedAsDraft.pipe(takeUntil(this.onDestroy))
      .subscribe(saveAsDraft => {
        this.isSavedAsDraft = saveAsDraft;
      });

    this.commonService
      .onNavigationEnd
      .pipe(takeUntil(this.onDestroy))
      .subscribe(d => {
        const urlParams = {
          params: this.route.snapshot.params,
          queryParams: this.route.snapshot.queryParams
        }

        const prevUrlParams = this.commonService.urlParams.getValue();
        
        if (d.isRouteChanged || JSON.stringify(prevUrlParams) !== JSON.stringify(urlParams)) {
         this.commonService.urlParams.next(urlParams);
        }
      });
  }

  addCategories() {
    const categoryModel = this.matDialog.open(AddCategoriesComponent, {
      width: '60%', minHeight: '60%', data: {
        categories: this.addResourceForm.value.categories
      }
    });
    categoryModel.afterClosed().pipe(takeUntil(this.onDestroy))
      .subscribe(categories => {
        if (categories?.type === 'confirm') {
          const categoriesData: any[] = []
          categories?.data?.formValues?.categories.map(category => {
            if (category.name !== '') {
              categoriesData.push(category)
            }
          })
          this.selectedCategoriesDataMap = [...categories.data.categoriesDataMap]
          this.addResourceForm.setValue({...this.addResourceForm.value, categories: [...categoriesData]});
        }
      })
  }

  addResources() {
    const resourceModel = this.matDialog.open(RelatedResourcesComponent, {
      width: '60%', height: '85%', data: {
        relatedResources: this.addResourceForm.value.relatedResources
      }
    });
    resourceModel.afterClosed().pipe(takeUntil(this.onDestroy))
      .subscribe(selectedResources => {
        this.store.dispatch(FetchRelatedResourcesRequest({
          payload: {
            pageNumber: 1, filters: {
              searchText: ''
            }
          }
        }))
        if (selectedResources.type === 'confirm') {
          if (selectedResources?.data.length > 0) {
            const relatedResources: any[] = []
            selectedResources?.data.map(resource => {
              relatedResources.push(resource)
            })
            this.addResourceForm.setValue({...this.addResourceForm.value, relatedResources: [...relatedResources]});
          } else {
            this.addResourceForm.setValue({...this.addResourceForm.value, relatedResources: []});
          }
        }
      })
  }

  addSection(): void {
    const sectionUuid: string = uuidv4();
    const newSection: Section = {
      title: '',
      uuid: sectionUuid,
      content: [
        {
          uuid: uuidv4(),
          layout: [
            {
              width: 100,
              contentType: '',
              uuid: uuidv4()
            }
          ]
        }
      ]
    }
    const section: any[] = (this.resourceDetails.sections && this.resourceDetails?.sections?.length > 0) ? [...this.resourceDetails.sections] : []
    section.push(newSection)
    this.resourceHubService.setLastUpdatedId(sectionUuid)
    this.store.dispatch(UpdateSectionDetailsAddResources({section: section}))
  }

  checkForErrors(currentField?: string): void {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.addResourceForm, this.errorMessageMap, currentField))
    };
  }

  onClickAddButton(): void {
    if (this.addResourceForm.invalid) {
      this.checkForErrors()
      return
    }
  }

  sectionDeleteClickHandler(sectionToBeRemovedIndex: number): void {
    const section: Section[] = [...this.resourceDetails.sections];
    section.splice(sectionToBeRemovedIndex, 1);
    this.store.dispatch(UpdateSectionDetailsAddResources({section: section}));
  }

  shiftSectionElementUp(indexToBeMovedUp: number): void {
    const movedToIndex = indexToBeMovedUp - 1;
    const sections: Section[] = [...this.resourceDetails.sections]
    this.changeElementSequence(sections, indexToBeMovedUp, movedToIndex)
  }

  shiftSectionElementDown(indexToBeMovedUp: number): void {
    const movedToIndex = indexToBeMovedUp + 1;
    const sections: Section[] = [...this.resourceDetails.sections]
    this.changeElementSequence(sections, indexToBeMovedUp, movedToIndex);
  }

  changeElementSequence(sectionArray: any, from: number, to: number): void {
    let cutOut = sectionArray.splice(from, 1) [0];
    sectionArray.splice(to, 0, cutOut);
    this.store.dispatch(UpdateSectionDetailsAddResources({section: sectionArray}));
  }

  onSectionValueChanges(event): void {
    let section: Section[] = [...this.resourceDetails.sections];
    const indexToBeUpdated = section.findIndex(item => item.uuid === event.uuid);
    if (indexToBeUpdated > -1) {
      section[indexToBeUpdated] = event;
      this.store.dispatch(UpdateSectionDetailsAddResources({section: section}))
    }
  }

  onImageSelection(event): void {
    this.imageLoading = true;
    const selectedFile = event[0];
    if (selectedFile) {
      const fileExtension = selectedFile.type;
      const fileSize = selectedFile.size / 1024 / 1024;
      if (!this.allowExtension.includes(fileExtension)) {
        this.checkForErrors();
        this.commonService.notification('Supported formats are png, jpg, jpeg, webp', 'warning')
        return
      }
      if (fileSize > 5) {
        this.checkForErrors();
        this.commonService.notification('Image size should be less than 5MB', 'warning');
        return
      }
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const result = e.target.result;
        const blob = new Blob([result], {type: selectedFile.type});
        this.uploadImage(selectedFile, blob)
      }
      reader.readAsArrayBuffer(selectedFile);

    } else {
      this.commonService.notification('File is not selected , please try again', 'warning');
    }
  }

  uploadImage(selectedFile, blob) {
    const fileName = selectedFile.name;
    const contentType = selectedFile.type;
    let imagePath = ''
    this.sharedService.getSignedUrlForImageUpload({fileName, contentType})
      .pipe(takeUntil(this.onDestroy), switchMap((signedUrl) => {
        const formData: FormData = new FormData();
        formData.append('file', blob);
        imagePath = signedUrl?.data?.path
        return this.sharedService.uploadImageToCdn(signedUrl?.data?.uploadUrl, blob, contentType)
      })).subscribe(signedUrl => {
      this.addResourceForm.patchValue({
        imageUrl: imagePath
      })
      this.imageLoading = false
    }, err => {
      this.imageLoading = false
    })
  }

  deleteSelectedImage(): void {
    this.imageLoading = true;
    this.sharedService.deleteUploadedImage({
      path: this.addResourceForm.value.imageUrl
    }).pipe(takeUntil(this.onDestroy)).subscribe(response => {
      this.addResourceForm.patchValue({
        imageUrl: ''
      })
      this.imageLoading = false;
    }, err => {
      this.addResourceForm.patchValue({
        imageUrl: ''
      });
      this.imageLoading = false;
    })
  }

  getSelectedSessionName(sessionId: string): string {
    const selectedSession = this.sessionDropDownList.filter(session => session.key === sessionId)
    return selectedSession[0]?.value
  }

  getSelectedRolesName(roleId: string): string {
    const selectedRole = this.roleOptions?.filter(role => role.key === roleId)
    return selectedRole?.[0]?.value
  }

  getSelectedOrganisationName(orgId: number): string {
    const selected = this.organisationOptions?.filter(org => org.key === orgId)
    return selected?.[0]?.value
  }

  getSelectedGroupName(groupId: number): string {
    const selected = this.groupOptions?.filter(group => group.key === groupId)
    return selected?.[0]?.value
  }

  toggleAllOrganisations() {
    this.allOrganisations = !this.allOrganisations;
    if (this.allOrganisations) {
      this.addResourceForm.patchValue({organisations: []});
    }
  }

  toggleAllGroups() {
    this.allGroups = !this.allGroups;
    if (this.allGroups) {
      this.addResourceForm.patchValue({groups: []});
    }
  }

  getCategoriesName(categories: any[]): string {
    if (categories && categories?.length > 0) {
      return categories.map(category => category.value).join(', ')
    } else {
      return ''
    }
  }

  getSubCategoriesName(categories: any[]) {
    if (categories && categories?.length > 0) {
      return categories.map(category => category.subCategories.map(subCategories => subCategories.value)
        .join(', ')).join(', ')
    } else {
      return ''
    }
  }

  getSelectedCategoryName(categoryId: string) {
    return this.categoriesData.filter(category => category.key === categoryId)[0]?.value
  }

  getSelectedSubCategoryName(categoryId: string, subCategoryId: string) {
    const selectedCategories = this.categoriesData.filter(category => category.key === categoryId)[0]
    return selectedCategories?.subCategories?.filter(subCategory => subCategory.key === subCategoryId)[0]?.value
  }

  actionButtonClicked(actionType: "published" | "draft" | "inActive") {
    const updatedResourceDetails: ResourceDetails = {
      ...this.resourceDetails,
      status: (CreateResourceStatus[actionType] as number)
    }

    // Set session as 0 if no session is selected
    if (updatedResourceDetails?.roles?.length && !updatedResourceDetails?.sessions?.length) {
      updatedResourceDetails.sessions = [0] as any;
    }

    this.store.dispatch(SaveOrPublishResourceRequest({payload: {resource: updatedResourceDetails}}))
  }

  fetchSubCategoriesData() {
    this.resourceDetails.categories.forEach(categories => {
      this.store.dispatch(FetchSubCategoriesListRequest({payload: {categoryId: +categories.name}}))
    })
  }

  deleteResource() {
    this.store.dispatch(DeleteResourceRequest({payload: {resourceId: this.resourceDetails.id}}))
  }

  closeEditor() {
    if (this.mode === 'edit') {
      this.router.navigate([`/stanford-admin/resource-hub/resource-details/${this.resourceDetails.id}/${this.resourceDetails.slug}`])
    } else {
      this.router.navigate([`/stanford-admin/resource-hub/resources-list`])
    }
  }

  previewButtonClicked() {
    this.router.navigate([`/stanford-admin/resource-hub/preview-resource/${this.resourceDetails.id}/${this.resourceDetails.slug}`])
  }

  openRelatedResource(data){
    const urlPrefix = this.router.url.split('/')[1];
    this.commonService.openInNewTab(`/${urlPrefix}/resource-hub/resource-details/${data.id}/${data.slug}`)
  }

  trackBySection(index: number, section: any): string {
    return section.uuid || index.toString();
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
